import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { fetchExams } from './dashboard-actions'

import { ExamCard } from 'common/types/entities'
import { RequestStatus } from 'common/types/request-status'

export type ExamCardDomain = ExamCard & {
  result: null | number
  attempts: number
}

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    examCards: [] as ExamCardDomain[],
    status: 'idle' as RequestStatus,
    error: null as null | string,
    isInFrame: false,
    isDarkTheme: false,
  },
  reducers: {
    setExamCards(state, action: PayloadAction<{ examCards: ExamCard[] }>) {
      state.examCards = action.payload.examCards.map(card => {
        const persistedCard = state.examCards.find(savedCard => savedCard.id === card.id)

        if (persistedCard) {
          return {
            ...card,
            result: persistedCard.result,
            attempts: persistedCard.attempts,
          }
        } else {
          return {
            ...card,
            result: null as null | number,
            attempts: 0,
          }
        }
      })
    },
    setResult(state, action: PayloadAction<{ id: string; result: number }>) {
      return {
        ...state,
        examCards: state.examCards.map(card =>
          card.id === action.payload.id
            ? {
                ...card,
                result: action.payload.result,
                attempts: card.attempts + 1,
              }
            : card
        ),
      }
    },
    resetResults(state) {
      return {
        ...state,
        examCards: state.examCards.map(card => ({
          ...card,
          result: null,
          attempts: 0,
        })),
      }
    },
    setIsInFrame(state, action: PayloadAction<{ isInFrame: boolean }>) {
      state.isInFrame = action.payload.isInFrame
    },
    setIsDarkTheme(state, action: PayloadAction<{ isDarkTheme: boolean }>) {
      state.isDarkTheme = action.payload.isDarkTheme
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchExams.fulfilled, (state, action) => {
      state.examCards = action.payload!.map(card => {
        const persistedCard = state.examCards.find(savedCard => savedCard.id === card.id)

        if (persistedCard) {
          return {
            ...card,
            result: persistedCard.result,
            attempts: persistedCard.attempts,
          }
        } else {
          return {
            ...card,
            result: null as null | number,
            attempts: 0,
          }
        }
      })
    })
    builder.addMatcher(
      action => action.type.endsWith('/pending'),
      state => {
        state.status = 'loading'
        state.error = null
      }
    )
    builder.addMatcher(
      action => action.type.endsWith('/fulfilled'),
      state => {
        state.status = 'succeeded'
        state.error = null
      }
    )
    builder.addMatcher(
      action => action.type.endsWith('/rejected'),
      (state, action) => {
        state.status = 'failed'
        state.error = action.payload || action.error.message
      }
    )
  },
})

export const { setExamCards, setResult, resetResults, setIsInFrame, setIsDarkTheme } =
  dashboardSlice.actions

export const dashboardReducer = dashboardSlice.reducer
